import { useImmutableChannel } from '@/api/hooks'
import { cn } from '@/util/cn'
import { IChannel, IGif } from '@giphy/js-types'
import { Gif } from '@giphy/react-components'
import { ImgHTMLAttributes, useContext } from 'react'
import { LockIcon } from 'ui/src/icons/utility'
import { ModalContext } from '../modal'

const gifWidth = 144
const gifHeight = 88

export const PlaceholderImage = ({
    className,
    width = gifWidth,
    height = gifHeight,
    ...rest
}: ImgHTMLAttributes<HTMLImageElement>) => {
    return (
        <img
            width={width}
            height={height}
            alt=""
            className={cn('pointer-events-none block object-cover select-none', className)}
            style={{ height, width }}
            {...rest}
        />
    )
}
export const newCollectionImage = 'https://media.giphy.com/media/fTytsfKCVyb6lB75jM/giphy.gif'
const noFeaturedGifImage = 'https://media.giphy.com/media/Id66GDfKacJzxSvhqV/giphy.gif'
export const floatCollectionImage = 'https://giphy.com/static/img/backgrounds/floating-shapes-transparent.gif'

export const MoreChildrenBadge = ({ count }: { count: number }) => (
    <div className="absolute top-2 right-2 rounded-lg border bg-giphyBlack/80 p-1 text-center text-xs font-bold text-giphyWhite">
        +{count} more
    </div>
)

// Shown on a card the caller has marked selected. Only the creation tools'
// collection picker passes `selectedChannelIds` today — the add-to-collection
// flows act on a click instead of accumulating a selection, so they never
// render this. Reproduces legacy's `IconCheck` (shared/components/creation/
// info-fields/fields/channels.tsx:49-64): an aqua disc with a black check.
export const SelectedBadge = () => (
    <div
        className="absolute top-2 right-2 flex size-5 items-center justify-center rounded-full bg-giphyAqua text-[9px] text-black"
        data-testid="collection-selected-badge"
    >
        <i className="ss-check" />
    </div>
)

export const ChannelContent = ({ channel }: { channel: IChannel }) => (
    <>
        <CollectionGif gif={channel.featured_gif} isPrivate={channel.is_private} />
        <CollectionLabel label={channel.short_display_name || channel.display_name} />
        <div className={cn(gifBorderClassName, 'absolute inset-0')}></div>
    </>
)

export const Breadcrumb = ({ channel, className }: { channel: IChannel; className?: string }) => {
    const { setModalType } = useContext(ModalContext)
    return (
        <span
            className={cn('cursor-pointer hover:text-giphyBlue', className)}
            onClick={() => {
                setModalType({
                    name: 'editCollections',
                    channelId: channel.id,
                    title: <Breadcrumbs channel={channel} />,
                })
            }}
        >
            {channel.display_name}
        </span>
    )
}
export const Breadcrumbs = ({ channel }: { channel: IChannel }) => {
    const { data } = useImmutableChannel(channel.id)
    const ancestors = data?.ancestors ? [...data.ancestors, channel] : [channel]
    return ancestors.map((channel, index) => {
        const isLast = index === ancestors.length - 1
        return (
            <div key={channel.id}>
                <Breadcrumb channel={channel} className={cn(isLast && 'pointer-events-none text-giphyLightGrey')} />
                {!isLast && <span className="mx-1">/</span>}
            </div>
        )
    })
}

export const CollectionGif = ({
    gif,
    isPrivate,
    className,
}: {
    className?: string
    gif?: IGif
    isPrivate?: boolean
}) => {
    return (
        <>
            {gif ? (
                <Gif
                    className={cn(className, '[&_img]:object-cover')}
                    gif={gif}
                    percentWidth="100%"
                    width={gifWidth}
                    height={gifHeight}
                    hideAttribution
                    noLink
                    borderRadius={0}
                    backgroundColor={gif.is_sticker ? undefined : 'rgba(0,0,0,0)'}
                />
            ) : (
                <PlaceholderImage src={noFeaturedGifImage} className={cn(className)} width="100%" height={gifHeight} />
            )}
            {isPrivate && (
                <div className="absolute inset-0 flex items-center justify-center">
                    {/* @ts-ignore use css instead of color prop */}
                    <LockIcon height={25} width={40} color={null} className="fill-giphyRed" />
                </div>
            )}
        </>
    )
}

export const CollectionLabel = ({ className, label }: { label: string; className?: string }) => {
    return (
        <div
            className={cn('overflow-hidden px-2.5 py-1.5 text-sm font-bold text-ellipsis whitespace-nowrap', className)}
            style={{ textShadow: '#000 1px 1px 2px' }}
        >
            {label}
        </div>
    )
}

export const Divider = () => {
    return <hr className="my-3 w-full border-giphyDarkGrey" />
}

export const inputClassName = 'bg-giphyDarkGrey text-giphyWhite  box-border h-9  border-0 p-2.5'

export const cardClassName = 'relative flex cursor-pointer select-none flex-col overflow-hidden rounded-md border-solid'

export const colorsClassName = cn(
    `[&:nth-child(1n)]:bg-giphyAqua [&:nth-child(2n)]:bg-giphyRed [&:nth-child(3n)]:bg-giphyIndigo [&:nth-child(4n)]:bg-giphyPink [&:nth-child(5n)]:bg-giphyPurple`,
    `hover:[&:nth-child(1n)]:border-giphyAqua hover:[&:nth-child(2n)]:border-giphyRed hover:[&:nth-child(3n)]:border-giphyIndigo hover:[&:nth-child(4n)]:border-giphyPink hover:[&:nth-child(5n)]:border-giphyPurple`
)

export const gifBorderClassName = 'box-border border-solid border-inherit group-hover:border-4 rounded-md'
